import React, { useState } from "react";
import SEO from "../components/SEO";
import structuredData from "../data/structuredData.json";
import Aboutbg from "../images/about-sec-bg.svg";
import ContactFormSlider from "../components/ContactFormSlider";
import Header from "../components/Header";
import { IconHeadphones } from "@tabler/icons-react";
import { IconAutomation } from "@tabler/icons-react";
import { IconMessage } from "@tabler/icons-react";
import { IconPhone } from "@tabler/icons-react";
import { IconMapPin } from "@tabler/icons-react";

const CONTACT_ENDPOINT = process.env.REACT_APP_CONTACT_ENDPOINT || "/contact";

const Contact = () => {
  // Form state
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    work_email: "",
    company_website: "",
    company_size: "",
    country: "",
    how_did_you_find_us_short: "",
    how_did_you_find_us_message: "",
    honeypot: "",
  });

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({
    success: false,
    message: "",
  });

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ success: false, message: "" });

    try {
      const response = await fetch(CONTACT_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const responseData = await response.json().catch(() => ({}));

      if (!response.ok) {
        const errorMessage =
          responseData?.errors?.[0] ||
          responseData?.message ||
          "Sorry, there was an error submitting your message. Please try again.";
        throw new Error(errorMessage);
      }

      setSubmitStatus({
        success: true,
        message:
          responseData?.message ||
          "Thank you for your message! We will get back to you soon.",
      });

      setFormData({
        first_name: "",
        last_name: "",
        work_email: "",
        company_website: "",
        company_size: "",
        country: "",
        how_did_you_find_us_short: "",
        how_did_you_find_us_message: "",
        honeypot: "",
      });
    } catch (error) {
      setSubmitStatus({
        success: false,
        message:
          error?.message ||
          "Sorry, there was an error submitting your message. Please try again.",
      });
      console.error("Error submitting form:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Contact GXL Engineering | Precision Engineering Malaysia"
        description="Contact GXL Engineering for precision engineering, CNC machining, and automation solutions in Malaysia. Speak with our team today."
        url="/contact"
        type="website"
        noSuffix
        structuredData={structuredData.contact}
      />
      {/* ..........  Hero Section .....  */}
      <section
        style={{
          backgroundImage: `url(${Aboutbg})`,
          backgroundPosition: "50% 0",
        }}
        className="bg-no-repeat bg-cover max-w-none  relative">
        <Header />
        <div className="container lg:px-10 px-6 max-w-[1600px] mx-auto lg:py-20 py-10">
          <div className="flex flex-col-reverse md:grid md:grid-cols-2 gap-8">
            <div className="flex flex-col justify-between">
              <div>
                <div className="space-y-2">
                  <h1 className="lg:text-5xl leading-normal md:text-4xl text-3xl font-semibold text-start text-white">
                    Let's build something exceptional together.
                  </h1>
                  <p className="text-[#fff] lg:text-lg text-base font-normal text-start ">
                    Whether you're exploring integration options, need technical
                    guidance, or want to discuss a custom solution, our expert
                    team is here to help.
                  </p>
                </div>
                <div className="space-y-4 mt-4">
                  <h2 className="text-white lg:text-3xl text-xl font-medium">
                    Why reach out?
                  </h2>
                  <div className="space-y-4 mt-10">
                    <div className="flex items-start gap-4 mt-3 ">
                      <div className="relative z-10 flex h-12 w-12 items-center justify-center overflow-hidden rounded-full bg-[#2a2932]  min-h-12 min-w-12">
                        <IconHeadphones className="text-[#62c5ba]" stroke={2} />
                      </div>
                      <p className="text-[#fff]/60 text-base font-normal lg:max-w-[70%]">
                        <span className="text-white font-semibold text-lg">
                          Free Consultation –{" "}
                        </span>{" "}
                        Get expert advice from engineers with 30+ years of
                        experience
                      </p>
                    </div>
                    <div className="flex items-start gap-4 mt-3 ">
                      {/* <img src={IconForm} className='w-12' /> */}
                      <div className="relative z-10 flex h-12 w-12 items-center justify-center overflow-hidden rounded-full bg-[#2a2932]  min-h-12 min-w-12">
                        <IconAutomation className="text-[#e37e5d]" stroke={2} />
                      </div>
                      <p className="text-[#fff]/60 text-base font-normal lg:max-w-[70%]">
                        <span className="text-white font-semibold text-lg">
                          {" "}
                          Custom Solutions –{" "}
                        </span>
                        Precision-engineered tooling and fixtures tailored to
                        your production tolerances.
                      </p>
                    </div>
                    <div className="flex items-start gap-4 mt-3 ">
                      {/* <img src={IconForm} className='w-12' /> */}
                      <div className="relative z-10 flex h-12 w-12 items-center justify-center overflow-hidden rounded-full bg-[#2a2932]  min-h-12 min-w-12">
                        <IconMessage className="text-[#956BCF]" stroke={2} />
                      </div>
                      <p className="text-[#fff]/60 text-base font-normal lg:max-w-[70%]">
                        <span className="text-white font-semibold text-lg">
                          Reliable Support –
                        </span>{" "}
                        Responsive engineers ready to resolve machining
                        challenges and keep your lines running.
                      </p>
                    </div>
                  </div>
                </div>
                <p className="text-[#fff]/60 font-normal lg:text-lg text-base text-start mt-2">
                  that integrate seamlessly into your systems—so you can focus
                  on creating amazing solutions.{" "}
                </p>
              </div>
              <h2 className="text-white lg:text-xl text-lg font-medium mt-2">
                Reach us at
              </h2>
              <ul className="text-[#fff]/60 font-light space-y-3 mt-2">
                <li className="flex items-center gap-3">
                  <span className="flex p-2 items-center justify-center rounded-full bg-[#2a2932]">
                    <IconPhone stroke={1.5} />
                  </span>
                  <span>012-6815982</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex p-2 items-center justify-center rounded-full bg-[#2a2932] mt-0.5">
                    <IconMapPin stroke={1.5} />
                  </span>
                  <span>
                    No.2582-2583, Jalan RJ 1/20, Light Industrial Area, Taman
                    Rasah Jaya, 70300 Seremban, Negeri Sembilan Darul Khusus,
                    Malaysia.
                  </span>
                </li>
              </ul>
              <div className="mt-4 text-[#fff]/70">
                <p className="font-semibold text-white">
                  GXL Engineering &amp; Trading Sdn Bhd
                </p>
                <p className="text-sm">SSM : 201001009239</p>
              </div>
              <ContactFormSlider />
            </div>
            <div className="relative rounded-2xl px-8 pb-10 pt-[68px] lg:px-6 sm:px-5 sm:pb-8 bg-[#0B0C0E] bg-[radial-gradient(82.95%_35.21%_at_77.88%_98.92%,_rgba(115,_89,_58,_0.12)_23.77%,_rgba(90,_79,_65,_0.00)_80.53%),_radial-gradient(76.32%_33.64%_at_47.78%_4.3%,_rgba(63,_67,_98,_0.20)_15.23%,_rgba(63,_67,_98,_0.00)_80.53%)]">
              <form onSubmit={handleSubmit}>
                <div
                  style={{
                    position: "absolute",
                    left: "-10000px",
                    top: "auto",
                    width: "1px",
                    height: "1px",
                    overflow: "hidden",
                  }}
                  aria-hidden="true">
                  <label>
                    Do not fill this field
                    <input
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      name="honeypot"
                      value={formData.honeypot}
                      onChange={handleInputChange}
                    />
                  </label>
                </div>
                <div className="grid sm:grid-cols-2 grid-cols-1 gap-6">
                  <div>
                    <label className="space-y-2">
                      <span className="block text-base font-normal leading-none text-[#fff]/90">
                        First name
                      </span>
                      <div className="mt-1.5 h-11 relative flex flex-col items-start">
                        <input
                          className="border transition-colors duration-200 focus:outline-none bg-black w-full border tracking-tight placeholder:tracking-tight h-11 rounded px-3 border-[#41434D] hover:border-[#707280] focus:border-[#A3A6B2] text-white placeholder:text-[#8A8C99]"
                          type="text"
                          placeholder="John"
                          name="first_name"
                          value={formData.first_name}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </label>
                  </div>
                  <div>
                    <label className="space-y-2">
                      <span className="block text-base font-normal leading-none text-[#fff]/90">
                        Last name
                      </span>
                      <div className="mt-1.5 h-11 relative flex flex-col items-start">
                        <input
                          className="border transition-colors duration-200 focus:outline-none bg-black w-full border tracking-tight placeholder:tracking-tight h-11 rounded px-3 border-[#41434D] hover:border-[#707280] focus:border-[#A3A6B2] text-white placeholder:text-[#8A8C99]"
                          type="text"
                          placeholder="Doe"
                          name="last_name"
                          value={formData.last_name}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </label>
                  </div>
                  <div>
                    <label className="space-y-2">
                      <span className="block text-base font-normal leading-none text-[#fff]/90">
                        Work email
                      </span>
                      <div className="mt-1.5 h-11 relative flex flex-col items-start">
                        <input
                          className="border transition-colors duration-200 focus:outline-none bg-black w-full border tracking-tight placeholder:tracking-tight h-11 rounded px-3 border-[#41434D] hover:border-[#707280] focus:border-[#A3A6B2] text-white placeholder:text-[#8A8C99]"
                          type="email"
                          placeholder="name@company.com"
                          name="work_email"
                          value={formData.work_email}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </label>
                  </div>
                  <div>
                    <label className="space-y-2">
                      <span className="block text-base font-normal leading-none text-[#fff]/90">
                        Company website
                      </span>
                      <div className="mt-1.5 h-11 relative flex flex-col items-start">
                        <input
                          className="border transition-colors duration-200 focus:outline-none bg-black w-full border tracking-tight placeholder:tracking-tight h-11 rounded px-3 border-[#41434D] hover:border-[#707280] focus:border-[#A3A6B2] text-white placeholder:text-[#8A8C99]"
                          type="url"
                          placeholder="https://www.acme.com"
                          name="company_website"
                          value={formData.company_website}
                          onChange={handleInputChange}
                        />
                      </div>
                    </label>
                  </div>
                  <div>
                    <label className="space-y-2">
                      <span className="block text-base font-normal leading-none text-[#fff]/90">
                        Company size
                      </span>
                      <div className="relative mt-1.5">
                        <select
                          name="company_size"
                          className="h-11 w-full max-w-full appearance-none rounded border border-[#41434D] bg-black px-3 tracking-tight transition-colors duration-200 placeholder:tracking-tight placeholder:text-[#8A8C99] hover:border-[#707280] focus:outline-none text-white"
                          value={formData.company_size}
                          onChange={handleInputChange}>
                          <option value="">Select an option</option>
                          <option value="1-20 employees">1-20 employees</option>
                          <option value="20-100 employees">
                            20-100 employees
                          </option>
                          <option value="100-1000 employees">
                            100-1000 employees
                          </option>
                          <option value="1000+ employees">
                            1000+ employees
                          </option>
                        </select>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 16 17"
                          className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#A3A6B2]">
                          <path
                            stroke="currentColor"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="1.6"
                            d="m14 5.5-6 6-6-6"></path>
                        </svg>
                      </div>
                    </label>
                  </div>
                  <div>
                    <label className="space-y-2">
                      <span className="block text-base font-normal leading-none text-[#fff]/90">
                        Country
                      </span>
                      <div className="relative mt-1.5">
                        <select
                          name="country"
                          className="h-11 w-full max-w-full appearance-none rounded border border-[#41434D] bg-black px-3 tracking-tight transition-colors duration-200 placeholder:tracking-tight placeholder:text-[#8A8C99] hover:border-[#707280] focus:outline-none text-white"
                          value={formData.country}
                          onChange={handleInputChange}
                          required>
                          <option value="">Select a country</option>
                          <option value="Afghanistan">Afghanistan</option>
                          <option value="Albania">Albania</option>
                          <option value="Algeria">Algeria</option>
                          <option value="American Samoa">American Samoa</option>
                          <option value="Andorra">Andorra</option>
                          <option value="Angola">Angola</option>
                          <option value="Anguilla">Anguilla</option>
                          <option value="Antarctica">Antarctica</option>
                          <option value="Antigua and Barbuda">
                            Antigua and Barbuda
                          </option>
                          <option value="Argentina">Argentina</option>
                          <option value="Armenia">Armenia</option>
                          <option value="Aruba">Aruba</option>
                          <option value="Australia">Australia</option>
                          <option value="Austria">Austria</option>
                          <option value="Azerbaijan">Azerbaijan</option>
                          <option value="Bahamas">Bahamas</option>
                          <option value="Bahrain">Bahrain</option>
                          <option value="Bangladesh">Bangladesh</option>
                          <option value="Barbados">Barbados</option>
                          <option value="Belarus">Belarus</option>
                          <option value="Belgium">Belgium</option>
                          <option value="Belize">Belize</option>
                          <option value="Benin">Benin</option>
                          <option value="Bermuda">Bermuda</option>
                          <option value="Bhutan">Bhutan</option>
                          <option value="Bolivia">Bolivia</option>
                          <option value="Bonaire, Sint Eustatius and Saba">
                            Bonaire, Sint Eustatius and Saba
                          </option>
                          <option value="Bosnia and Herzegovina">
                            Bosnia and Herzegovina
                          </option>
                          <option value="Botswana">Botswana</option>
                          <option value="Bouvet Island">Bouvet Island</option>
                          <option value="Brazil">Brazil</option>
                          <option value="British Indian Ocean Territory">
                            British Indian Ocean Territory
                          </option>
                          <option value="Brunei Darussalam">
                            Brunei Darussalam
                          </option>
                          <option value="Bulgaria">Bulgaria</option>
                          <option value="Burkina Faso">Burkina Faso</option>
                          <option value="Burundi">Burundi</option>
                          <option value="Cambodia">Cambodia</option>
                          <option value="Cameroon">Cameroon</option>
                          <option value="Canada">Canada</option>
                          <option value="Cape Verde">Cape Verde</option>
                          <option value="Cayman Islands">Cayman Islands</option>
                          <option value="Central African Republic">
                            Central African Republic
                          </option>
                          <option value="Chad">Chad</option>
                          <option value="Chile">Chile</option>
                          <option value="China">China</option>
                          <option value="Christmas Island">
                            Christmas Island
                          </option>
                          <option value="Cocos (Keeling) Islands">
                            Cocos (Keeling) Islands
                          </option>
                          <option value="Colombia">Colombia</option>
                          <option value="Comoros">Comoros</option>
                          <option value="Congo, Republic of the (Brazzaville)">
                            Congo, Republic of the (Brazzaville)
                          </option>
                          <option value="Congo, the Democratic Republic of the (Kinshasa)">
                            Congo, the Democratic Republic of the (Kinshasa)
                          </option>
                          <option value="Cook Islands">Cook Islands</option>
                          <option value="Costa Rica">Costa Rica</option>
                          <option value="Croatia">Croatia</option>
                          <option value="Cuba">Cuba</option>
                          <option value="Curaçao">Curaçao</option>
                          <option value="Cyprus">Cyprus</option>
                          <option value="Czech Republic">Czech Republic</option>
                          <option value="Côte d'Ivoire, Republic of">
                            Côte d'Ivoire, Republic of
                          </option>
                          <option value="Denmark">Denmark</option>
                          <option value="Djibouti">Djibouti</option>
                          <option value="Dominica">Dominica</option>
                          <option value="Dominican Republic">
                            Dominican Republic
                          </option>
                          <option value="Ecuador">Ecuador</option>
                          <option value="Egypt">Egypt</option>
                          <option value="El Salvador">El Salvador</option>
                          <option value="Equatorial Guinea">
                            Equatorial Guinea
                          </option>
                          <option value="Eritrea">Eritrea</option>
                          <option value="Estonia">Estonia</option>
                          <option value="Eswatini">Eswatini</option>
                          <option value="Ethiopia">Ethiopia</option>
                          <option value="Falkland Islands (Islas Malvinas)">
                            Falkland Islands (Islas Malvinas)
                          </option>
                          <option value="Faroe Islands">Faroe Islands</option>
                          <option value="Fiji">Fiji</option>
                          <option value="Finland">Finland</option>
                          <option value="France">France</option>
                          <option value="French Guiana">French Guiana</option>
                          <option value="French Polynesia">
                            French Polynesia
                          </option>
                          <option value="French Southern and Antarctic Lands">
                            French Southern and Antarctic Lands
                          </option>
                          <option value="Gabon">Gabon</option>
                          <option value="Gambia, The">Gambia, The</option>
                          <option value="Georgia">Georgia</option>
                          <option value="Germany">Germany</option>
                          <option value="Ghana">Ghana</option>
                          <option value="Gibraltar">Gibraltar</option>
                          <option value="Greece">Greece</option>
                          <option value="Greenland">Greenland</option>
                          <option value="Grenada">Grenada</option>
                          <option value="Guadeloupe">Guadeloupe</option>
                          <option value="Guam">Guam</option>
                          <option value="Guatemala">Guatemala</option>
                          <option value="Guernsey">Guernsey</option>
                          <option value="Guinea">Guinea</option>
                          <option value="Guinea-Bissau">Guinea-Bissau</option>
                          <option value="Guyana">Guyana</option>
                          <option value="Haiti">Haiti</option>
                          <option value="Heard Island and McDonald Islands">
                            Heard Island and McDonald Islands
                          </option>
                          <option value="Holy See (Vatican City)">
                            Holy See (Vatican City)
                          </option>
                          <option value="Honduras">Honduras</option>
                          <option value="Hong Kong">Hong Kong</option>
                          <option value="Hungary">Hungary</option>
                          <option value="Iceland">Iceland</option>
                          <option value="India">India</option>
                          <option value="Indonesia">Indonesia</option>
                          <option value="Iran, Islamic Republic of">
                            Iran, Islamic Republic of
                          </option>
                          <option value="Iraq">Iraq</option>
                          <option value="Ireland">Ireland</option>
                          <option value="Isle of Man">Isle of Man</option>
                          <option value="Israel">Israel</option>
                          <option value="Italy">Italy</option>
                          <option value="Jamaica">Jamaica</option>
                          <option value="Japan">Japan</option>
                          <option value="Jersey">Jersey</option>
                          <option value="Jordan">Jordan</option>
                          <option value="Kazakhstan">Kazakhstan</option>
                          <option value="Kenya">Kenya</option>
                          <option value="Kiribati">Kiribati</option>
                          <option value="Korea, Democratic People's Republic of">
                            Korea, Democratic People's Republic of
                          </option>
                          <option value="Korea, Republic of">
                            Korea, Republic of
                          </option>
                          <option value="Kosovo">Kosovo</option>
                          <option value="Kuwait">Kuwait</option>
                          <option value="Kyrgyzstan">Kyrgyzstan</option>
                          <option value="Laos">Laos</option>
                          <option value="Latvia">Latvia</option>
                          <option value="Lebanon">Lebanon</option>
                          <option value="Lesotho">Lesotho</option>
                          <option value="Liberia">Liberia</option>
                          <option value="Libya">Libya</option>
                          <option value="Liechtenstein">Liechtenstein</option>
                          <option value="Lithuania">Lithuania</option>
                          <option value="Luxembourg">Luxembourg</option>
                          <option value="Macao">Macao</option>
                          <option value="Macedonia, Republic of">
                            Macedonia, Republic of
                          </option>
                          <option value="Madagascar">Madagascar</option>
                          <option value="Malawi">Malawi</option>
                          <option value="Malaysia">Malaysia</option>
                          <option value="Maldives">Maldives</option>
                          <option value="Mali">Mali</option>
                          <option value="Malta">Malta</option>
                          <option value="Marshall Islands">
                            Marshall Islands
                          </option>
                          <option value="Martinique">Martinique</option>
                          <option value="Mauritania">Mauritania</option>
                          <option value="Mauritius">Mauritius</option>
                          <option value="Mayotte">Mayotte</option>
                          <option value="Mexico">Mexico</option>
                          <option value="Micronesia, Federated States of">
                            Micronesia, Federated States of
                          </option>
                          <option value="Moldova">Moldova</option>
                          <option value="Monaco">Monaco</option>
                          <option value="Mongolia">Mongolia</option>
                          <option value="Montenegro">Montenegro</option>
                          <option value="Montserrat">Montserrat</option>
                          <option value="Morocco">Morocco</option>
                          <option value="Mozambique">Mozambique</option>
                          <option value="Myanmar">Myanmar</option>
                          <option value="Namibia">Namibia</option>
                          <option value="Nauru">Nauru</option>
                          <option value="Nepal">Nepal</option>
                          <option value="Netherlands">Netherlands</option>
                          <option value="New Caledonia">New Caledonia</option>
                          <option value="New Zealand">New Zealand</option>
                          <option value="Nicaragua">Nicaragua</option>
                          <option value="Niger">Niger</option>
                          <option value="Nigeria">Nigeria</option>
                          <option value="Niue">Niue</option>
                          <option value="Norfolk Island">Norfolk Island</option>
                          <option value="Northern Mariana Islands">
                            Northern Mariana Islands
                          </option>
                          <option value="Norway">Norway</option>
                          <option value="Oman">Oman</option>
                          <option value="Pakistan">Pakistan</option>
                          <option value="Palau">Palau</option>
                          <option value="Palestine">Palestine</option>
                          <option value="Panama">Panama</option>
                          <option value="Papua New Guinea">
                            Papua New Guinea
                          </option>
                          <option value="Paraguay">Paraguay</option>
                          <option value="Peru">Peru</option>
                          <option value="Philippines">Philippines</option>
                          <option value="Pitcairn">Pitcairn</option>
                          <option value="Poland">Poland</option>
                          <option value="Portugal">Portugal</option>
                          <option value="Puerto Rico">Puerto Rico</option>
                          <option value="Qatar">Qatar</option>
                          <option value="Romania">Romania</option>
                          <option value="Russian Federation">
                            Russian Federation
                          </option>
                          <option value="Rwanda">Rwanda</option>
                          <option value="Réunion">Réunion</option>
                          <option value="Saint Barthélemy">
                            Saint Barthélemy
                          </option>
                          <option value="Saint Helena, Ascension and Tristan da Cunha">
                            Saint Helena, Ascension and Tristan da Cunha
                          </option>
                          <option value="Saint Kitts and Nevis">
                            Saint Kitts and Nevis
                          </option>
                          <option value="Saint Lucia">Saint Lucia</option>
                          <option value="Saint Martin">Saint Martin</option>
                          <option value="Saint Pierre and Miquelon">
                            Saint Pierre and Miquelon
                          </option>
                          <option value="Saint Vincent and the Grenadines">
                            Saint Vincent and the Grenadines
                          </option>
                          <option value="Samoa">Samoa</option>
                          <option value="San Marino">San Marino</option>
                          <option value="Sao Tome and Principe">
                            Sao Tome and Principe
                          </option>
                          <option value="Saudi Arabia">Saudi Arabia</option>
                          <option value="Senegal">Senegal</option>
                          <option value="Serbia">Serbia</option>
                          <option value="Seychelles">Seychelles</option>
                          <option value="Sierra Leone">Sierra Leone</option>
                          <option value="Singapore">Singapore</option>
                          <option value="Sint Maarten (Dutch part)">
                            Sint Maarten (Dutch part)
                          </option>
                          <option value="Slovakia">Slovakia</option>
                          <option value="Slovenia">Slovenia</option>
                          <option value="Solomon Islands">
                            Solomon Islands
                          </option>
                          <option value="Somalia">Somalia</option>
                          <option value="South Africa">South Africa</option>
                          <option value="South Georgia and South Sandwich Islands">
                            South Georgia and South Sandwich Islands
                          </option>
                          <option value="South Sudan">South Sudan</option>
                          <option value="Spain">Spain</option>
                          <option value="Sri Lanka">Sri Lanka</option>
                          <option value="Sudan">Sudan</option>
                          <option value="Suriname">Suriname</option>
                          <option value="Sweden">Sweden</option>
                          <option value="Switzerland">Switzerland</option>
                          <option value="Syrian Arab Republic">
                            Syrian Arab Republic
                          </option>
                          <option value="Taiwan">Taiwan</option>
                          <option value="Tajikistan">Tajikistan</option>
                          <option value="Tanzania, United Republic of">
                            Tanzania, United Republic of
                          </option>
                          <option value="Thailand">Thailand</option>
                          <option value="Timor-Leste">Timor-Leste</option>
                          <option value="Togo">Togo</option>
                          <option value="Tokelau">Tokelau</option>
                          <option value="Tonga">Tonga</option>
                          <option value="Trinidad and Tobago">
                            Trinidad and Tobago
                          </option>
                          <option value="Tunisia">Tunisia</option>
                          <option value="Turkey">Turkey</option>
                          <option value="Turkmenistan">Turkmenistan</option>
                          <option value="Turks and Caicos Islands">
                            Turks and Caicos Islands
                          </option>
                          <option value="Tuvalu">Tuvalu</option>
                          <option value="Uganda">Uganda</option>
                          <option value="Ukraine">Ukraine</option>
                          <option value="United Arab Emirates">
                            United Arab Emirates
                          </option>
                          <option value="United Kingdom">United Kingdom</option>
                          <option value="United States">United States</option>
                          <option value="United States Minor Outlying Islands">
                            United States Minor Outlying Islands
                          </option>
                          <option value="Uruguay">Uruguay</option>
                          <option value="Uzbekistan">Uzbekistan</option>
                          <option value="Vanuatu">Vanuatu</option>
                          <option value="Venezuela, Bolivarian Republic of">
                            Venezuela, Bolivarian Republic of
                          </option>
                          <option value="Vietnam">Vietnam</option>
                          <option value="Virgin Islands, British">
                            Virgin Islands, British
                          </option>
                          <option value="Virgin Islands, U.S.">
                            Virgin Islands, U.S.
                          </option>
                          <option value="Wallis and Futuna">
                            Wallis and Futuna
                          </option>
                          <option value="Western Sahara">Western Sahara</option>
                          <option value="Yemen">Yemen</option>
                          <option value="Zambia">Zambia</option>
                          <option value="Zimbabwe">Zimbabwe</option>
                          <option value="Åland Islands">Åland Islands</option>
                        </select>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 16 17"
                          className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#A3A6B2]">
                          <path
                            stroke="currentColor"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="1.6"
                            d="m14 5.5-6 6-6-6"></path>
                        </svg>
                      </div>
                    </label>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="space-y-2">
                      <span className="block text-base font-normal leading-none text-[#fff]/90">
                        How did you find us?
                      </span>
                      <div className="mt-1.5 h-11 relative flex flex-col items-start">
                        <input
                          className="border transition-colors duration-200 focus:outline-none bg-black w-full border tracking-tight placeholder:tracking-tight h-11 rounded px-3 border-[#41434D] hover:border-[#707280] focus:border-[#A3A6B2] text-white placeholder:text-[#8A8C99]"
                          type="text"
                          placeholder="Recommended by a friend..."
                          name="how_did_you_find_us_short"
                          value={formData.how_did_you_find_us_short}
                          onChange={handleInputChange}
                        />
                      </div>
                    </label>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="space-y-2">
                      <span className="block text-base font-normal leading-none text-[#fff]/90">
                        Message
                      </span>
                      <div className="mt-1.5 flex flex-col items-start">
                        <textarea
                          className="remove-autocomplete-styles appearance-none border transition-colors duration-200 focus:outline-none bg-black w-full border tracking-tight placeholder:tracking-tight h-11 rounded px-3 min-h-[134px] pb-[13px] pt-[11px] border-[#41434D] hover:border-[#707280] focus:border-[#A3A6B2] text-[#fff]/90 placeholder:text-[#8A8C99]"
                          name="how_did_you_find_us_message"
                          placeholder="Your message..."
                          value={formData.how_did_you_find_us_message}
                          onChange={handleInputChange}
                          required></textarea>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Status message */}
                {submitStatus.message && (
                  <div
                    className={`mt-4 p-4 rounded ${
                      submitStatus.success
                        ? "bg-green-900/20 text-green-400"
                        : "bg-red-900/20 text-red-400"
                    }`}>
                    {submitStatus.message}
                  </div>
                )}

                <div className="md:flex justify-between items-center gap-x-5 mt-6 sm:gap-y-6 md:space-y-0 space-y-4">
                  <div className="mt-3">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <p className="m-0 text-sm text-white">
                        By submitting this, I confirm that I have read and{" "}
                        <br className="lg:hidden" />
                        understood the{" "}
                        <a
                          className="duration-200 group inline-flex items-center text-base text-[#C36D1D] font-medium"
                          href="/">
                          Privacy Policy
                        </a>
                        .
                      </p>
                    </label>
                  </div>
                  <div>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="whitespace-nowrap transition-colors duration-300 group inline-flex items-center outline-none justify-center tracking-tight leading-none focus:outline-white focus:outline-1 focus:outline-offset-4 h-10 text-[15px] px-16 rounded-full md:h-10 shadow-[0px_2px_2px_0px_rgba(0,0,0,0.74)] font-semibold relative disabled:opacity-50 disabled:cursor-not-allowed">
                      <span className="absolute -inset-px bottom-[-1.5px] rounded-full bg-[linear-gradient(180deg,#fcc171_0%,#C17C56_50%,#362821_100%)]"></span>
                      <span className="absolute -top-[5px] bottom-0.5 left-1/2 w-[91%] -translate-x-1/2 bg-btn-glowing mix-blend-screen blur-[1px] transition-transform duration-300 ease-in-out group-hover:translate-y-[-2px]"></span>
                      <span className="absolute inset-0 rounded-full bg-black"></span>
                      <span className="absolute inset-0 rounded-full bg-btn-glowing-inset opacity-100 transition-opacity duration-300 ease-in-out group-hover:opacity-0"></span>
                      <span className="absolute inset-0 rounded-full bg-btn-glowing-inset-hover opacity-0 transition-opacity duration-300 ease-in-out group-hover:opacity-100"></span>
                      <span className="relative z-20 bg-[linear-gradient(180deg,#FFF_33.33%,#E4D0B1_116.67%)] bg-clip-text text-transparent">
                        {isSubmitting ? "Submitting..." : "Submit"}
                      </span>
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
